import React, { useState } from 'react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { PlusCircle, FileText, Calendar, Users, BarChart2, AlertTriangle } from 'lucide-react';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const STATS = [
  { key: 'releases',  label: 'Press Releases',  sub: 'Active Releases',    icon: FileText  },
  { key: 'events',    label: 'Events',           sub: 'Upcoming Events',    icon: Calendar  },
  { key: 'contacts',  label: 'Media Contacts',   sub: 'Active Contacts',    icon: Users     },
  { key: 'coverage',  label: 'Coverage',         sub: 'Media Mentions',     icon: BarChart2 },
];

const EmptyPanel = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-12 gap-2">
    <div
      className="flex items-center justify-center w-10 h-10 rounded-lg"
      style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
    >
      <FileText size={18} style={{ color: SYSTEM_COLORS.textSoft }} />
    </div>
    <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{message}</p>
  </div>
);

const ActionButton = ({ permission, label, icon: Icon, danger }) => {
  const { hasPermission } = useApiAuth();
  const allowed = hasPermission(permission);
  return (
    <button
      disabled={!allowed}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
      style={{
        backgroundColor: allowed ? (danger ? SYSTEM_COLORS.danger : BRAND_COLORS.primary) : SYSTEM_COLORS.surfaceMuted,
        color: allowed ? '#fff' : SYSTEM_COLORS.textMuted,
      }}
    >
      <Icon size={16} />
      {label}
    </button>
  );
};

const PRPage = () => {
  const [pressReleases] = useState([]);
  const [events] = useState([]);
  const [mediaContacts] = useState([]);

  const counts = { releases: pressReleases.length, events: events.length, contacts: mediaContacts.length, coverage: 0 };

  return (
    <div className="min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
            Communications
          </p>
          <h1 className="mt-1 text-2xl font-bold sm:text-3xl" style={{ color: NEUTRAL_COLORS.black }}>
            Public Relations
          </h1>
          <p className="mt-1 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
            Manage media relations and public communications
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map(({ key, label, sub, icon: Icon }) => (
            <div
              key={key}
              className="flex items-center gap-3 px-4 py-4 rounded-xl border"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
            >
              <div
                className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}
              >
                <Icon size={18} style={{ color: BRAND_COLORS.primary }} />
              </div>
              <div>
                <p className="text-xl font-bold leading-none" style={{ color: BRAND_COLORS.primary }}>{counts[key]}</p>
                <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Panels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Press Releases */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Press Releases</h2>
              <ActionButton permission="pr.createPressRelease" label="New Release" icon={PlusCircle} />
            </div>
            <div className="p-4">
              <EmptyPanel message="No press releases yet" />
            </div>
          </div>

          {/* Events */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Events</h2>
              <ActionButton permission="pr.manageEvents" label="New Event" icon={PlusCircle} />
            </div>
            <div className="p-4">
              <EmptyPanel message="No events scheduled" />
            </div>
          </div>

          {/* Media Contacts */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Media Contacts</h2>
              <ActionButton permission="pr.manageMediaContacts" label="Add Contact" icon={PlusCircle} />
            </div>
            <div className="p-4">
              <EmptyPanel message="No media contacts added" />
            </div>
          </div>

          {/* Crisis Communication */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
          >
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <h2 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Crisis Communication</h2>
              <ActionButton permission="pr.manageCrisis" label="New Alert" icon={AlertTriangle} danger />
            </div>
            <div className="p-4">
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
              >
                <p className="text-sm font-semibold mb-1" style={{ color: NEUTRAL_COLORS.black }}>Crisis Protocols</p>
                <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                  Access pre-approved response templates and communication protocols for crisis situations.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PRPage;
